import { useEffect, useRef } from 'react';

// Plays a short two-tone chime using the Web Audio API (no audio asset needed).
let audioCtx = null;
let unlocked = false;
let pendingChime = false;

const getCtx = () => {
    if (!audioCtx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtx = new Ctx();
    }
    return audioCtx;
};

export const playChime = () => {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    [[880, 0], [1174.66, 0.18]].forEach(([freq, offset]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.35, now + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.5);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + offset);
        osc.stop(now + offset + 0.55);
    });
};

// Browsers block audio until the user has interacted with the page once.
const unlockOnGesture = () => {
    if (unlocked) return;
    const handler = () => {
        unlocked = true;
        const ctx = getCtx();
        if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
        if (pendingChime) {
            pendingChime = false;
            playChime();
        }
        window.removeEventListener('click', handler);
        window.removeEventListener('keydown', handler);
    };
    window.addEventListener('click', handler);
    window.addEventListener('keydown', handler);
};

const newestTimestamp = (list) =>
    (list || []).reduce((max, n) => {
        const t = n && n.createdAt ? new Date(n.createdAt).getTime() : 0;
        return t > max ? t : max;
    }, 0);

/**
 * Chimes whenever the notification list contains an item newer than the last
 * one seen for this user (persisted in localStorage so a page reload is silent).
 * Returns nothing; call with the notifications array and a per-user storage key.
 */
export const useNotificationChime = (list, storageKey) => {
    const lastSeen = useRef(null);

    useEffect(() => {
        unlockOnGesture();
    }, []);

    useEffect(() => {
        if (!list || !list.length || !storageKey) return;
        const newest = newestTimestamp(list);
        if (!newest) return;

        if (lastSeen.current === null) {
            const stored = Number(localStorage.getItem(storageKey) || 0);
            lastSeen.current = stored || newest;
            if (!stored) localStorage.setItem(storageKey, String(newest));
        }

        if (newest > lastSeen.current) {
            lastSeen.current = newest;
            localStorage.setItem(storageKey, String(newest));
            if (unlocked) playChime();
            else pendingChime = true;
        }
    }, [list, storageKey]);
};

export default useNotificationChime;
